/**
 * nonLocalApiTarget — pure check for whether the dev build is pointed at a
 * non-local API host. No React, no import.meta — callers pass in the two
 * raw values so this stays unit-testable with plain `node:test`.
 */

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '0.0.0.0']);
const LOCAL_HOSTNAME_SUFFIX = '.localhost';

export interface NonLocalApiTargetInput {
  isDev: boolean;
  apiBaseUrl: string | undefined;
}

/**
 * Returns the non-local host (`host:port`) the dev build is talking to, or
 * `null` when there is nothing to warn about — not in dev mode, no base URL
 * configured, the base URL is a relative path (proxied through Vite, e.g.
 * `/api`), or the host resolves to localhost/127.0.0.1.
 */
export function resolveNonLocalApiTarget({
  isDev,
  apiBaseUrl,
}: NonLocalApiTargetInput): string | null {
  if (!isDev || !apiBaseUrl) {
    return null;
  }
  let parsed: URL;
  try {
    parsed = new URL(apiBaseUrl);
  } catch {
    return null;
  }
  if (LOCAL_HOSTNAMES.has(parsed.hostname) || parsed.hostname.endsWith(LOCAL_HOSTNAME_SUFFIX)) {
    return null;
  }
  return parsed.host;
}
